import { mediaReference } from '@/services/storage/scopedMedia'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import { useUserContext } from '@/context/UserContext'
import {
  fetchSupportPayload,
  runSupportCommand,
  useSupportTarget,
} from '@/services/support/support.client'
import type { SupportTarget } from '@/services/support/supportContract'
import { uploadFile } from '@/services/storage'
import { PROJECT_IMAGE_MEDIA_WRITES_ENABLED } from '@/services/storage/projectImageMediaGate'
import {
  assertCurrentProjectImage,
  type ProjectImageUpload,
  publishNewProjectImage,
} from '@/services/storage/projectImageMedia'
import {
  createOrgMemberMtn,
  createProjectMutation,
  getProjectQuery,
  updateProjectMutation,
} from '@/services/graphql'
import { ProjectMembershipCreationError } from '@/services/errors/projectCreationErrors'
import { trackUsageEvent, type UsageEventProps } from '@/utils/posthog.utils'

const client = getAmplifyClient()

const trackProjectUsage = (props: UsageEventProps<'feature_used'>) => {
  try {
    trackUsageEvent('feature_used', props)
  } catch (error) {
    console.error('failed to track project usage', error)
  }
}

export const useCreateProject = () => {
  const supportTarget = useSupportTarget()
  const { organizationScope } = useUserContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['createProject'],
    mutationFn: (variables: Parameters<typeof createProject>[0]) =>
      createProject(
        variables,
        supportTarget,
        organizationScope.activeOrganizationId,
      ),
    retry: false,
    onSuccess: (_data, variables) => {
      const { project } = variables
      queryClient.refetchQueries({
        queryKey: ['projects'],
      })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      trackProjectUsage({
        feature: 'projects',
        action: 'create_project',
        surface: 'projects_overview',
        result: 'success',
        has_media: Boolean(project.picture),
      })
    },
  })
}

export const useUpdateProject = () => {
  const supportTarget = useSupportTarget()
  const { organizationScope } = useUserContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['updateProject'],
    mutationFn: (variables: Parameters<typeof updateProject>[0]) =>
      updateProject(
        variables,
        supportTarget,
        organizationScope.activeOrganizationId,
      ),
    retry: false,
    onSuccess: (_data, variables) => {
      const { project } = variables
      queryClient.refetchQueries({
        queryKey: ['projects'],
      })
      queryClient.refetchQueries({ queryKey: ['project', project.id] })
      trackProjectUsage({
        feature: 'projects',
        action: 'update_project',
        surface: 'project_details',
        result: 'success',
        has_media: Boolean(project.picture),
      })
    },
  })
}

export const createProject = async (
  { project, member },
  supportTarget?: SupportTarget | null,
  organizationID?: string,
) => {
  try {
    if (project.picture) {
      const uploadResult = await uploadFile({
        key: project.picture,
        file: project.pictureFile,
      })
      if (uploadResult === undefined) {
        throw new Error(`Upload failed for key: ${project.picture}`)
      }
      delete project.pictureFile
    }
    if (supportTarget) {
      await runSupportCommand(
        'CreateProject',
        supportTarget,
        organizationID ?? project.organizationID,
        { ...project, progress: project.progress ?? 'active' },
      )
    } else
      await client.graphql({
        query: createProjectMutation,
        variables: {
          input: {
            ...project,
            progress: project.progress ?? 'active',
          },
        },
      })
  } catch (e) {
    console.error(e)
    throw e
  }

  try {
    if (supportTarget) {
      await runSupportCommand(
        'CreateOrganizationMember',
        supportTarget,
        organizationID ?? project.organizationID,
        { ...member, onSeat: false },
      )
    } else
      await client.graphql({
        query: createOrgMemberMtn,
        variables: {
          input: { ...member, onSeat: false },
        },
      })
  } catch (e) {
    console.error(e)
    throw new ProjectMembershipCreationError(project.id, { cause: e })
  }
}

const matchesProjectInput = (
  input: Record<string, unknown>,
  saved: Record<string, unknown>,
) =>
  Object.entries(input)
    .filter(([, value]) => value !== undefined)
    .every(([key, value]) => {
      const observed =
        key === 'placeProjectsId'
          ? (saved.place as { id?: string } | null)?.id
          : saved[key]
      return JSON.stringify(observed) === JSON.stringify(value)
    })

function isProjectWriteTransportError(error: unknown): boolean {
  const failure = error as {
    name?: string
    status?: number
    errors?: { errorType?: string; originalError?: { name?: string } }[]
  }
  if ([401, 403].includes(failure?.status ?? 0)) return false
  // Amplify api-graphql wraps its REST NetworkError in GraphQLError.originalError.
  // Server GraphQL errors (and mixed results) do not have this transport provenance.
  if (failure?.errors?.length)
    return failure.errors.every(
      (entry) =>
        !entry.errorType && entry.originalError?.name === 'NetworkError',
    )
  return error instanceof TypeError || failure?.name === 'NetworkError'
}

export const updateProject = async (
  {
    project,
    imageUpload,
  }: {
    project: Record<string, any>
    imageUpload?: ProjectImageUpload
  },
  supportTarget?: SupportTarget | null,
  organizationID?: string,
) => {
  try {
    const { pictureFile, ...input } = project
    if (PROJECT_IMAGE_MEDIA_WRITES_ENABLED && pictureFile && !imageUpload)
      throw new Error('Scoped project image upload context is required')
    if (imageUpload) {
      if (
        imageUpload.intent.selection.projectID !== input.id ||
        imageUpload.intent.selection.file !== pictureFile
      ) {
        throw new Error('Project image resource changed')
      }
      const publication = await publishNewProjectImage(imageUpload)
      input.picture = mediaReference(publication)
      assertCurrentProjectImage(imageUpload)
    } else if (pictureFile) {
      const uploadResult = await uploadFile({
        key: project.picture,
        file: pictureFile,
      })
      if (uploadResult === undefined) {
        throw new Error(`Upload failed for key: ${project.picture}`)
      }
    }

    try {
      if (supportTarget) {
        await runSupportCommand(
          'UpdateProject',
          supportTarget,
          organizationID ?? input.organizationID,
          input,
        )
      } else
        await client.graphql({
          query: updateProjectMutation,
          variables: { input },
        })
    } catch (error) {
      // Only an ambiguous transport failure may be reconciled. An explicit
      // GraphQL/security rejection is never converted into a successful save.
      if (!imageUpload || !isProjectWriteTransportError(error)) throw error
      assertCurrentProjectImage(imageUpload)
      const readback = supportTarget
        ? {
            data: {
              getProject: await fetchSupportPayload<Record<string, unknown>>(
                `query ConsoleSupportProject($targetUserID: ID!, $organizationID: ID!, $id: ID!) {
          consoleSupportProject(targetUserID: $targetUserID, organizationID: $organizationID, id: $id) { payload }
        }`,
                'consoleSupportProject',
                {
                  targetUserID: supportTarget.targetUserID,
                  organizationID: organizationID ?? input.organizationID,
                  id: input.id,
                },
              ),
            },
          }
        : await client.graphql<{
            getProject: Record<string, unknown> | null
          }>({
            query: getProjectQuery,
            variables: { id: input.id },
          })
      assertCurrentProjectImage(imageUpload)
      // Matching the picture alone cannot attest the other submitted form fields.
      if (
        readback.errors?.length ||
        !readback.data?.getProject ||
        !matchesProjectInput(input, readback.data.getProject)
      )
        throw error
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}
